import React, { useCallback, useEffect, useState } from 'react';
import {
  cantidadEmpresaVencimientoPorIndicador,
  EMPRESA_VENCIMIENTO_COLORS,
  EMPRESA_VENCIMIENTO_INDICADOR_PROXIMO,
  EMPRESA_VENCIMIENTO_INDICADOR_VENCIDO,
  EMPRESA_VENCIMIENTO_INDICADORES,
  type EmpresaVencimientoFilterParam,
  type EmpresaVencimientoResumen,
  fetchEmpresaVencimientoFielResumen,
  fetchEmpresaVencimientoSellosResumen,
} from './empresa-vencimiento-resumen';

interface EmpresaVencimientoVencidoIndicatorsProps {
  onIndicadorClick: (
    param: EmpresaVencimientoFilterParam,
    indicador: (typeof EMPRESA_VENCIMIENTO_INDICADORES)[number]['id'],
  ) => void;
}

function VencimientoStatBadge({
  label,
  value,
  color,
  loading,
  onClick,
}: {
  label: string;
  value: number;
  color: string;
  loading?: boolean;
  onClick?: () => void;
}) {
  const clickable = !!onClick && !loading && value > 0;

  return (
    <div
      style={{
        ...styles.statBadge,
        borderLeft: `4px solid ${color}`,
        cursor: clickable ? 'pointer' : undefined,
      }}
      onClick={clickable ? onClick : undefined}
      onKeyDown={
        clickable
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      <span style={{ ...styles.statValue, color }}>{loading ? '…' : value}</span>
      <span style={styles.statLabel}>{label}</span>
    </div>
  );
}

function VencimientoIndicadorBlock({
  title,
  loading,
  resumen,
  filterParam,
  onIndicadorClick,
}: {
  title: string;
  loading: boolean;
  resumen: EmpresaVencimientoResumen[];
  filterParam: EmpresaVencimientoFilterParam;
  onIndicadorClick: EmpresaVencimientoVencidoIndicatorsProps['onIndicadorClick'];
}) {
  const proximo = cantidadEmpresaVencimientoPorIndicador(
    resumen,
    EMPRESA_VENCIMIENTO_INDICADOR_PROXIMO,
  );
  const vencido = cantidadEmpresaVencimientoPorIndicador(
    resumen,
    EMPRESA_VENCIMIENTO_INDICADOR_VENCIDO,
  );

  return (
    <div style={styles.statBlock}>
      <p style={styles.statBlockSubtitle}>{title}</p>
      <div style={styles.statStrip}>
        <VencimientoStatBadge
          label="Próximo a vencer"
          value={proximo}
          color={EMPRESA_VENCIMIENTO_COLORS['Proximo a Vencer']}
          loading={loading}
          onClick={() => onIndicadorClick(filterParam, EMPRESA_VENCIMIENTO_INDICADOR_PROXIMO)}
        />
        <VencimientoStatBadge
          label="Vencido"
          value={vencido}
          color={EMPRESA_VENCIMIENTO_COLORS.Vencido}
          loading={loading}
          onClick={() => onIndicadorClick(filterParam, EMPRESA_VENCIMIENTO_INDICADOR_VENCIDO)}
        />
      </div>
    </div>
  );
}

export default function EmpresaVencimientoVencidoIndicators({
  onIndicadorClick,
}: EmpresaVencimientoVencidoIndicatorsProps) {
  const [sellosResumen, setSellosResumen] = useState<EmpresaVencimientoResumen[]>([]);
  const [fielResumen, setFielResumen] = useState<EmpresaVencimientoResumen[]>([]);
  const [loadingSellos, setLoadingSellos] = useState(false);
  const [loadingFiel, setLoadingFiel] = useState(false);

  const loadResumen = useCallback(async () => {
    setLoadingSellos(true);
    setLoadingFiel(true);
    const [sellos, fiel] = await Promise.all([
      fetchEmpresaVencimientoSellosResumen(),
      fetchEmpresaVencimientoFielResumen(),
    ]);
    setSellosResumen(sellos);
    setFielResumen(fiel);
    setLoadingSellos(false);
    setLoadingFiel(false);
  }, []);

  useEffect(() => {
    loadResumen();
  }, [loadResumen]);

  return (
    <div style={styles.wrap}>
      <VencimientoIndicadorBlock
        title="Vencimiento de Sellos"
        loading={loadingSellos}
        resumen={sellosResumen}
        filterParam="vencimientoSellosEstado"
        onIndicadorClick={onIndicadorClick}
      />
      <VencimientoIndicadorBlock
        title="Vencimiento de Fiel"
        loading={loadingFiel}
        resumen={fielResumen}
        filterParam="vencimientoFielEstado"
        onIndicadorClick={onIndicadorClick}
      />
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 24,
    flex: '1 1 480px',
    minWidth: 0,
  },
  statBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    flex: '1 1 320px',
  },
  statBlockSubtitle: {
    margin: 0,
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--crm-text-primary)',
  },
  statStrip: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
  },
  statBadge: {
    background: 'var(--crm-surface)',
    borderRadius: 10,
    padding: '14px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
    flex: '1 1 160px',
    maxWidth: 220,
  },
  statValue: {
    fontSize: 28,
    fontWeight: 700,
    lineHeight: 1,
  },
  statLabel: {
    fontSize: 12,
    color: 'var(--crm-text-secondary)',
    fontWeight: 500,
  },
};
